import { z } from "zod";

/**
 * Validates that a media URL is either empty (allowed in drafts)
 * or a safe HTTP/HTTPS or safe relative URL, strictly rejecting
 * dangerous schemes like javascript:, data:, vbscript:.
 */
export function isSafeMediaUrl(val: unknown): boolean {
  if (typeof val !== "string") return false;
  const trimmed = val.trim();
  if (trimmed === "") return true; // Allowed in drafts
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return true;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export const safeMediaUrlSchema = z
  .string()
  .max(1024)
  .refine(isSafeMediaUrl, {
    message: "Must be a valid HTTP or HTTPS media URL",
  });

export const mediaItemSchema = z.object({
  id: z.string().optional(),
  url: safeMediaUrlSchema.default(""),
  alt: z.string().max(200).optional().default(""),
  caption: z.string().max(300).optional().default(""),
  linkUrl: safeMediaUrlSchema.optional().default(""),
});


import { getSectionDisplayOptionsSchema } from "../display-options";

export const mediaDisplayOptionsSchema = getSectionDisplayOptionsSchema("media");
export type MediaDisplayOptions = z.infer<typeof mediaDisplayOptionsSchema>;

export const mediaContentSchema = z.object({
  heading: z.string().max(120).optional().default(""),
  description: z.string().max(1000).optional().default(""),
  mediaType: z.enum(["image", "image-gallery", "carousel", "video", "spacer"]).default("image"),
  videoUrl: safeMediaUrlSchema.optional().default(""),
  items: z.array(mediaItemSchema).max(20).default([]),
  aspectRatio: z.enum(["auto", "16-9", "4-3", "1-1", "3-4"]).default("auto"),
  galleryLayout: z.enum(["stacked", "grid", "row"]).default("grid"),
  alignment: z.enum(["left", "center", "right"]).default("center"),
  spacerHeight: z.enum(["sm", "md", "lg", "xl"]).optional().default("md"),
  displayOptions: mediaDisplayOptionsSchema.optional(),
});

export type MediaItem = z.infer<typeof mediaItemSchema>;
export type MediaContent = z.infer<typeof mediaContentSchema>;
